'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { colors, formatNum, baseStyles } from './SharedUI'; 

const localStyles = {
  btn: { backgroundColor: colors.bg, color: colors.textMain, padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', transition: 'all 0.2s' }
};

export default function SupportResistanceRadarV2({ symbol, liveDataRef }) {
  // ==========================================
  // STATE: BẢN NHÁP (UI CHỜ LƯU)
  // ==========================================
  const [selectedDays, setSelectedDays] = useState(30); 
  const [pendingAuto, setPendingAuto] = useState(false); 

  // ==========================================
  // STATE: HỆ THỐNG ĐANG CHẠY THỰC TẾ (ACTIVE)
  // ==========================================
  const [activeDays, setActiveDays] = useState(30); 
  const [isAuto, setIsAuto] = useState(false); 
  
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const [macroData, setMacroData] = useState(null);
  const [liveScan, setLiveScan] = useState(null);
  const [showDetails, setShowDetails] = useState(true);

  // ==========================================
  // 1. TẢI & TÍNH TOÁN DỮ LIỆU VĨ MÔ
  // ==========================================
  const fetchMacroData = async (days) => {
    if (!symbol) return;
    setLoading(true);
    try {
      const res = await fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${symbol.toUpperCase()}&interval=1d&limit=${days}`);
      const klines = await res.json();

      if (!klines || klines.length === 0) return;

      const highs = klines.map(k => parseFloat(k[2]));
      const lows = klines.map(k => parseFloat(k[3]));
      const closes = klines.map(k => parseFloat(k[4]));

      const maxHigh = Math.max(...highs);
      const minLow = Math.min(...lows);
      const maxClose = Math.max(...closes);
      const minClose = Math.min(...closes);
      const range = maxHigh - minLow;

      const f786 = minLow + range * 0.786;
      const f618 = minLow + range * 0.618;
      const f500 = minLow + range * 0.5;
      const f382 = minLow + range * 0.382;
      const f236 = minLow + range * 0.236;

      const q75 = minLow + range * 0.75;
      const q25 = minLow + range * 0.25;

      const sellLower = Math.min(f786, q75, maxClose);
      const sellUpper = maxHigh;

      const buyLower = minLow;
      const buyUpper = Math.max(f236, q25, minClose);

      const pivotLower = f500 - (range * 0.015);
      const pivotUpper = f500 + (range * 0.015);

      const computedMacro = {
        maxHigh, minLow, maxClose, minClose, range,
        f786, f618, f500, f382, f236,
        q75, q25,
        zones: {
          sell: { lower: sellLower, upper: sellUpper, stars: 3 },
          pivot: { lower: pivotLower, upper: pivotUpper, stars: 2 },
          buy: { lower: buyLower, upper: buyUpper, stars: 3 }
        }
      };

      setMacroData(computedMacro);
      setActiveDays(days);
      
      scanLiveGPS(computedMacro);

    } catch (error) {
      console.error("Lỗi fetch Klines:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = () => {
    setIsAuto(pendingAuto); 
    fetchMacroData(selectedDays); 
  };

  // ==========================================
  // 2. QUÉT TỌA ĐỘ LIVE (GPS) VÀ TÍNH R:R
  // ==========================================
  const scanLiveGPS = useCallback((mData = macroData) => {
    if (!mData) return;
    
    const livePrice = parseFloat(liveDataRef?.current?.last || 0);
    
    if (!livePrice) {
      alert("Chưa có Dữ liệu Giá Live! Vui lòng chờ 2 giây rồi thử lại.");
      return;
    }

    const { zones } = mData;

    const distToSell = Math.max(0, zones.sell.lower - livePrice);
    const pctToSell = (distToSell / livePrice) * 100;

    const distToBuy = Math.max(0, livePrice - zones.buy.upper);
    const pctToBuy = (distToBuy / livePrice) * 100;

    let rrRatio = 0;
    if (distToBuy > 0) rrRatio = distToSell / distToBuy;
    else rrRatio = 99; 

    let positionStr = "⚠️ LƯNG CHỪNG ĐÈO (Mid-Range No Man's Land)";
    let actionStr = "🚫 Đứng ngoài (No Trade Zone). Chờ giá chạy về Hỗ trợ/Kháng cự rồi mới xét.";
    let rrDesc = `1 : ${rrRatio.toFixed(2)} (Rủi ro cao. Khoảng cách Lời < Lỗ)`;

    if (rrRatio > 1.5) rrDesc = `1 : ${rrRatio.toFixed(2)} (Khá Tốt. Ưu tiên LONG)`;
    if (rrRatio > 2.5) rrDesc = `1 : ${rrRatio.toFixed(2)} (QUÁ NGON. Setup chuẩn bài Quant)`;
    if (rrRatio < 0.5) rrDesc = `1 : ${rrRatio.toFixed(2)} (CỰC RỦI RO. Kháng cự quá gần)`;

    if (livePrice >= zones.sell.lower) {
      positionStr = "🧱 ĐANG TRONG VÙNG KHÁNG CỰ (Sell Zone)";
      actionStr = "🩸 TÌM ĐIỂM SHORT! Vùng hợp lưu Cản cứng. Quan sát dòng tiền Cá Mập 30m, nếu tụi nó Xả -> Gõ SHORT ngay.";
      rrDesc = `1 : ${rrRatio.toFixed(2)} (Biên độ Tăng đã cạn. Ưu tiên SHORT)`;
    } else if (livePrice <= zones.buy.upper) {
      positionStr = "🛡️ ĐANG TRONG VÙNG HỖ TRỢ (Buy Zone)";
      actionStr = "🚀 TÌM ĐIỂM LONG! Vùng hợp lưu Đỡ cứng. Quan sát dòng tiền Cá Mập 30m, nếu tụi nó Múc -> Kê LONG bắt đáy.";
      rrDesc = `1 : ${rrRatio.toFixed(2)} (Biên độ Giảm đã cạn. Ưu tiên LONG)`;
    } else if (livePrice >= zones.pivot.lower && livePrice <= zones.pivot.upper) {
      positionStr = "🧲 ĐANG MẮC KẸT TẠI TRỤC XOAY (Pivot)";
      actionStr = "⚖️ GIẰNG CO! Thường sẽ có rũ hàng hai đầu. Quan sát kỹ dòng tiền phân định thắng thua trước khi vào.";
    }

    setLiveScan({
      livePrice, distToSell, pctToSell, distToBuy, pctToBuy, rrRatio, rrDesc, positionStr, actionStr
    });
    setLastUpdate(new Date().toLocaleTimeString('vi-VN'));
  }, [macroData, liveDataRef]);

  // Logic Auto 15s
  useEffect(() => {
    let intervalId;
    if (isAuto && macroData) {
      intervalId = setInterval(() => { scanLiveGPS(); }, 15000);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [isAuto, macroData, scanLiveGPS]);

  useEffect(() => {
    fetchMacroData(activeDays);
    setIsAuto(false);
    setPendingAuto(false);
  }, [symbol]);

  // ==========================================
  // HELPER: Chèn Giá Live vào giữa các mốc
  // ==========================================
  const buildSortedList = (items, livePrice) => {
    const list = [...items, { label: '🔴 GIÁ LIVE HIỆN TẠI', val: livePrice, isLive: true }];
    return list.sort((a, b) => b.val - a.val); 
  };

  const renderSortedList = (sortedArray) => {
    return (
      <ul style={{ paddingLeft: '0', margin: '0', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {sortedArray.map((item, idx) => {
          // Linh hoạt số thập phân cho coin cỏ
          const decimals = item.val < 1 ? 6 : 4; 
          
          if (item.isLive) {
            return (
              <li key={idx} style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: colors.red, padding: '6px 12px', borderRadius: '6px', fontWeight: 'bold', border: `1px solid ${colors.red}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{item.label}</span>
                <span>${formatNum(item.val, decimals)}</span>
              </li>
            );
          }
          return (
            <li key={idx} style={{ color: colors.textSub, padding: '4px 12px', display: 'flex', justifyContent: 'space-between' }}>
              <span>{item.label}</span>
              <strong style={{ color: colors.textMain }}>${formatNum(item.val, decimals)}</strong>
            </li>
          );
        })}
      </ul>
    );
  };

  // ==========================================
  // 3. UI COMPONENTS
  // ==========================================
  const renderHeatmap = () => {
    if (!macroData || !liveScan) return null;
    const { minLow, range, zones } = macroData;
    const lp = liveScan.livePrice;

    const getPct = (val) => Math.max(0, Math.min(100, ((val - minLow) / range) * 100));

    const buyL = getPct(zones.buy.lower); const buyW = getPct(zones.buy.upper) - buyL;
    const pivL = getPct(zones.pivot.lower); const pivW = getPct(zones.pivot.upper) - pivL;
    const selL = getPct(zones.sell.lower); const selW = getPct(zones.sell.upper) - selL;
    const livePct = getPct(lp);

    const decimals = lp < 1 ? 6 : 4;

    return (
      <div style={{ padding: '10px 0', marginTop: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: colors.textSub, marginBottom: '6px' }}>
          <span>Wick Low: ${formatNum(minLow, decimals)}</span>
          <span>Wick High: ${formatNum(macroData.maxHigh, decimals)}</span>
        </div>
        <div style={{ position: 'relative', height: '14px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
          <div style={{ position: 'absolute', left: `${buyL}%`, width: `${buyW}%`, height: '100%', backgroundColor: colors.green, opacity: 0.8, borderRadius: '6px' }} title="Buy Zone"></div>
          <div style={{ position: 'absolute', left: `${pivL}%`, width: `${pivW}%`, height: '100%', backgroundColor: colors.warn, opacity: 0.8 }} title="Pivot Zone"></div>
          <div style={{ position: 'absolute', left: `${selL}%`, width: `${selW}%`, height: '100%', backgroundColor: colors.red, opacity: 0.8, borderRadius: '6px' }} title="Sell Zone"></div>
          <div style={{ position: 'absolute', left: `calc(${livePct}% - 7px)`, top: '-4px', width: '14px', height: '22px', backgroundColor: '#fff', borderRadius: '4px', boxShadow: '0 0 10px rgba(255,255,255,0.5)', zIndex: 10, border: `2px solid ${colors.bg}` }} title={`Live: ${lp}`}></div>
        </div>
        <div style={{ textAlign: 'center', fontSize: '12px', color: '#fff', marginTop: '8px', fontWeight: 'bold' }}>
          Giá Trục: 🔴 ${formatNum(lp, decimals)}
        </div>
      </div>
    );
  };

  const getDecimals = (val) => val < 1 ? 6 : 4;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '24px' }}>
      <div style={baseStyles.card}>
        
        {/* HEADER VÀ CẤU HÌNH */}
        <div style={{ borderBottom: `1px solid ${colors.border}`, paddingBottom: '16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <strong style={{ color: colors.textMain, fontSize: '16px' }}>🎯 HỆ THỐNG RADAR TÌM VÙNG CẢN HỢP LƯU V2 (S/R ĐỊNH LƯỢNG)</strong>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-between', alignItems: 'center' }}>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '8px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: `1px dashed ${colors.border}` }}>
              <span style={{ fontSize: '12px', color: colors.textSub }}>Khung soi Vĩ mô:</span>
              
              <select value={selectedDays} onChange={(e) => setSelectedDays(Number(e.target.value))} style={baseStyles.select}>
                <option value={7}>7 Ngày</option>
                <option value={15}>15 Ngày</option>
                <option value={30}>30 Ngày (Chuẩn)</option>
                <option value={45}>45 Ngày</option>
              </select>
              
              <div style={{ width: '1px', height: '16px', backgroundColor: colors.border, margin: '0 4px' }}></div>
              
              <div style={{ display: 'flex', backgroundColor: colors.bg, borderRadius: '6px', overflow: 'hidden', border: `1px solid ${colors.border}` }}>
                <button 
                  onClick={() => setPendingAuto(false)} 
                  style={{ padding: '4px 10px', fontSize: '12px', border: 'none', background: !pendingAuto ? 'rgba(255,255,255,0.1)' : 'transparent', color: !pendingAuto ? '#fff' : colors.textSub, cursor: 'pointer', fontWeight: 'bold' }}
                >MANUAL</button>
                <button 
                  onClick={() => setPendingAuto(true)} 
                  style={{ padding: '4px 10px', fontSize: '12px', border: 'none', background: pendingAuto ? colors.greenBg : 'transparent', color: pendingAuto ? colors.green : colors.textSub, cursor: 'pointer', fontWeight: 'bold' }}
                >AUTO 15s</button>
              </div>

              <button 
                onClick={handleSaveConfig} disabled={loading}
                style={{ ...localStyles.btn, backgroundColor: colors.bg, border: `1px solid ${colors.textMain}`, marginLeft: '8px' }}
              >
                {loading ? '⏳...' : '💾 LƯU & ÁP DỤNG'}
              </button>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button 
                onClick={() => scanLiveGPS()} 
                style={{ ...localStyles.btn, border: `1px solid ${colors.warn}`, color: colors.warn, backgroundColor: 'transparent' }}
              >
                ⚡ QUÉT TỌA ĐỘ LIVE NAY
              </button>
            </div>

          </div>
          
          <div style={{ fontSize: '11px', color: colors.textSub, marginTop: '8px', textAlign: 'right' }}>
            Trạng thái máy chủ: Khung <b>{activeDays} Ngày</b> | Chế độ: <strong style={{color: isAuto ? colors.green : colors.textMain}}>{isAuto ? '🟢 AUTO' : '🔘 MANUAL'}</strong> | Cập nhật Live: {lastUpdate || 'Chưa có'}
          </div>
        </div>

        {/* BẢNG ĐIỀU KHIỂN SAU KHI CÓ DATA */}
        {macroData && liveScan && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* 📡 ĐỊNH VỊ TOẠ ĐỘ LIVE (GPS) */}
            <div style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontWeight: 'bold', color: '#3b82f6', fontSize: '14px', marginBottom: '12px' }}>📡 ĐỊNH VỊ TOẠ ĐỘ LIVE (GPS)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', fontSize: '13px' }}>
                <div><span style={{ color: colors.textSub }}>🔴 Giá Live Hiện Tại:</span> <strong style={{ fontSize: '16px', color: colors.textMain }}>${formatNum(liveScan.livePrice, getDecimals(liveScan.livePrice))}</strong></div>
                <div><span style={{ color: colors.textSub }}>📍 Vị thế:</span> <strong style={{ color: colors.warn }}>{liveScan.positionStr}</strong></div>
                <div><span style={{ color: colors.textSub }}>⚖️ Lệch pha R:R:</span> <strong style={{ color: liveScan.rrRatio > 1.5 ? colors.green : (liveScan.rrRatio < 0.5 ? colors.red : colors.warn) }}>{liveScan.rrDesc}</strong></div>
              </div>
              {renderHeatmap()}
            </div>

            {/* 🎯 VÙNG NGẮM BẮN HỢP LƯU */}
            <div>
              <div style={{ fontWeight: 'bold', color: colors.textMain, fontSize: '14px', marginBottom: '12px', borderBottom: `1px dashed ${colors.border}`, paddingBottom: '8px' }}>
                🎯 VÙNG NGẮM BẮN HỢP LƯU (CONFLUENCE ZONES)
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                <div style={{ backgroundColor: colors.redBg, borderLeft: `4px solid ${colors.red}`, padding: '12px', borderRadius: '0 8px 8px 0' }}>
                  <div style={{ fontWeight: 'bold', color: colors.red, fontSize: '13px', marginBottom: '4px' }}>🧱 VÙNG KHÁNG CỰ (SELL ZONE) : [ ${formatNum(macroData.zones.sell.lower, getDecimals(macroData.zones.sell.lower))} - ${formatNum(macroData.zones.sell.upper, getDecimals(macroData.zones.sell.upper))} ]</div>
                  <div style={{ fontSize: '12px', color: colors.textSub, display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '8px' }}>
                    <div>🔥 Uy tín: [⭐⭐⭐] 3/3 (Fibo 0.786 + Quarter 75% + Đỉnh Nến Đóng Cửa)</div>
                    <div>📏 Khoảng cách: Cách Live <strong style={{ color: colors.textMain }}>${formatNum(liveScan.distToSell, getDecimals(liveScan.distToSell))} 📈 (Tăng +{liveScan.pctToSell.toFixed(2)}%)</strong></div>
                    <div>⏱️ Trạng thái: {liveScan.pctToSell < 2 ? <span style={{color: colors.warn}}>🚨 Sắp chạm Cản!</span> : "An toàn."}</div>
                  </div>
                </div>

                <div style={{ backgroundColor: colors.warnBg, borderLeft: `4px solid ${colors.warn}`, padding: '12px', borderRadius: '0 8px 8px 0' }}>
                  <div style={{ fontWeight: 'bold', color: colors.warn, fontSize: '13px', marginBottom: '4px' }}>🧲 TRỤC XOAY (PIVOT POINT) : [ ${formatNum(macroData.zones.pivot.lower, getDecimals(macroData.zones.pivot.lower))} - ${formatNum(macroData.zones.pivot.upper, getDecimals(macroData.zones.pivot.upper))} ]</div>
                  <div style={{ fontSize: '12px', color: colors.textSub, display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '8px' }}>
                    <div>⚠️ Uy tín: [⭐⭐] 2/3 (Fibo 0.5 + Quarter 50%)</div>
                    <div>📏 Khoảng cách Tới Trục: <strong style={{ color: colors.textMain }}>${formatNum(Math.abs(liveScan.livePrice - macroData.f500), getDecimals(Math.abs(liveScan.livePrice - macroData.f500)))} (Lệch {Math.abs(((liveScan.livePrice - macroData.f500)/liveScan.livePrice)*100).toFixed(2)}%)</strong></div>
                    <div>⏱️ Trạng thái: {Math.abs(((liveScan.livePrice - macroData.f500)/liveScan.livePrice)*100) < 2 ? <span style={{color: colors.red}}>🚨 ĐANG BỊ HÚT VỀ PIVOT!</span> : "Bình thường."}</div>
                  </div>
                </div>

                <div style={{ backgroundColor: colors.greenBg, borderLeft: `4px solid ${colors.green}`, padding: '12px', borderRadius: '0 8px 8px 0' }}>
                  <div style={{ fontWeight: 'bold', color: colors.green, fontSize: '13px', marginBottom: '4px' }}>🛡️ VÙNG HỖ TRỢ CỨNG (BUY ZONE) : [ ${formatNum(macroData.zones.buy.lower, getDecimals(macroData.zones.buy.lower))} - ${formatNum(macroData.zones.buy.upper, getDecimals(macroData.zones.buy.upper))} ]</div>
                  <div style={{ fontSize: '12px', color: colors.textSub, display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '8px' }}>
                    <div>🔥 Uy tín: [⭐⭐⭐] 3/3 (Fibo 0.236 + Quarter 25% + Đáy Nến Đóng Cửa)</div>
                    <div>📏 Khoảng cách: Cách Live <strong style={{ color: colors.textMain }}>${formatNum(liveScan.distToBuy, getDecimals(liveScan.distToBuy))} 📉 (Giảm -{liveScan.pctToBuy.toFixed(2)}%)</strong></div>
                    <div>⏱️ Trạng thái: {liveScan.pctToBuy < 2 ? <span style={{color: colors.warn}}>🚨 Sắp chạm Đáy!</span> : "Còn xa hỗ trợ."}</div>
                  </div>
                </div>

              </div>
            </div>

            {/* 🧠 BỘ NÃO KHUYẾN NGHỊ LỆNH */}
            <div style={{ backgroundColor: 'rgba(0,0,0,0.3)', padding: '16px', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
              <div style={{ fontSize: '14px', color: colors.textMain, marginBottom: '8px', fontWeight: 'bold' }}>🧠 AI TRADER KHUYẾN NGHỊ (Dựa trên GPS):</div>
              <div style={{ fontSize: '13px', color: colors.textSub, lineHeight: '1.6' }}>
                👉 TÌNH HÌNH: {liveScan.positionStr}.<br/>
                👉 KẾ HOẠCH TÁC CHIẾN: <strong style={{ color: liveScan.rrRatio > 1.5 ? colors.green : (liveScan.rrRatio < 0.5 ? colors.red : colors.warn) }}>{liveScan.actionStr}</strong><br/>
                <em>*Lưu ý: Luôn kết hợp quét "Dòng Tiền Cá Mập 30m" khi giá đi vào Zone để tránh bẫy đục cản/thủng nền.</em>
              </div>
            </div>

            <button 
              onClick={() => setShowDetails(!showDetails)}
              style={{ background: 'transparent', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '12px', textAlign: 'center', marginTop: '8px', padding: '10px', borderTop: `1px dashed rgba(255,255,255,0.1)` }}
            >
              {showDetails ? '🔼 ẨN CHI TIẾT 3 LỚP PHÂN TÍCH' : '🔽 MỞ BẢNG CHẨN ĐOÁN LIVE THEO 3 LỚP'}
            </button>

            {/* BẢNG CHẨN ĐOÁN LIVE 3 LỚP */}
            {showDetails && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '8px', fontSize: '12px', border: `1px solid ${colors.border}` }}>
                
                <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px' }}>
                  <strong style={{color: colors.textMain, fontSize: '13px', borderBottom: `1px solid ${colors.border}`, display: 'block', paddingBottom: '8px', marginBottom: '12px' }}>📐 Lớp 1: Fibonacci Retracement</strong>
                  {renderSortedList(buildSortedList([
                    { label: '0.786', val: macroData.f786 },
                    { label: '0.618', val: macroData.f618 },
                    { label: '0.500 (Trục Xoay)', val: macroData.f500 },
                    { label: '0.382', val: macroData.f382 },
                    { label: '0.236', val: macroData.f236 }
                  ], liveScan.livePrice))}
                </div>

                <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px' }}>
                  <strong style={{color: colors.textMain, fontSize: '13px', borderBottom: `1px solid ${colors.border}`, display: 'block', paddingBottom: '8px', marginBottom: '12px' }}>🍕 Lớp 2: Quarter Theory (4 Phần)</strong>
                  {renderSortedList(buildSortedList([
                    { label: 'Q75 (Vùng Xả Hàng)', val: macroData.q75 },
                    { label: 'Q50 (Trục Xoay)', val: macroData.f500 },
                    { label: 'Q25 (Vùng Gom Hàng)', val: macroData.q25 }
                  ], liveScan.livePrice))}
                </div>

                <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px' }}>
                  <strong style={{color: colors.textMain, fontSize: '13px', borderBottom: `1px solid ${colors.border}`, display: 'block', paddingBottom: '8px', marginBottom: '12px' }}>🧱 Lớp 3: Real Body (Thân/Râu)</strong>
                  {renderSortedList(buildSortedList([
                    { label: 'Wick High (Râu Đỉnh)', val: macroData.maxHigh },
                    { label: 'Close High (Cản Thân)', val: macroData.maxClose },
                    { label: 'Close Low (Đỡ Thân)', val: macroData.minClose },
                    { label: 'Wick Low (Râu Đáy)', val: macroData.minLow }
                  ], liveScan.livePrice))}
                </div>

              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
}
